import React, { useState } from 'react';
import {
  CG_BOOST_PLATFORMS,
  CgBoostCampaign,
  CgBoostPlatform,
  CgBoostPlatformIds,
  createCampaign,
  CreateCampaignRequest,
} from '../../services/cgBoostApi';
import { Modal, Button, Pill } from '../ui';
import PlatformLinkCard, { AnyPlatformIds } from './PlatformLinkCard';

interface NewCampaignModalProps {
  onClose: () => void;
  onCreated: (campaign: CgBoostCampaign) => void;
}

type Step = 'metadata' | 'linking';

const initial: CreateCampaignRequest = {
  brandName: '',
  campaignName: '',
  industryVertical: '',
  startDate: '',
  endDate: '',
  firstLineOfCopy: '',
  creators: [{ name: '' }],
  plannedBudget: { tiktok: 0, meta: 0, youtube: 0 },
  platformIds: {},
};

const VERTICALS = [
  'Apparel & Footwear',
  'Beauty & Personal Care',
  'CPG / Food & Beverage',
  'Telecommunications',
  'Auto',
  'Entertainment & Media',
  'Financial Services',
  'Tech / SaaS',
  'Other',
];

const NewCampaignModal: React.FC<NewCampaignModalProps> = ({ onClose, onCreated }) => {
  const [step, setStep] = useState<Step>('metadata');
  const [form, setForm] = useState<CreateCampaignRequest>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof CreateCampaignRequest>(key: K, value: CreateCampaignRequest[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateBudget = (platform: 'tiktok' | 'meta' | 'youtube', value: number) => {
    setForm((prev) => ({
      ...prev,
      plannedBudget: { ...prev.plannedBudget, [platform]: value },
    }));
  };

  const updatePlatformIds = (platform: CgBoostPlatform, ids: AnyPlatformIds | undefined) => {
    setForm((prev) => {
      const next: CgBoostPlatformIds = { ...(prev.platformIds ?? {}) };
      if (ids === undefined) {
        delete next[platform];
        return { ...prev, platformIds: next };
      }
      // Branch by platform so each assignment narrows to the correct slot
      // shape — replaces a Record<string, unknown> cast.
      switch (platform) {
        case 'tiktok':
          next.tiktok = ids as NonNullable<CgBoostPlatformIds['tiktok']>;
          break;
        case 'meta':
          next.meta = ids as NonNullable<CgBoostPlatformIds['meta']>;
          break;
        case 'youtube':
          next.youtube = ids as NonNullable<CgBoostPlatformIds['youtube']>;
          break;
      }
      return { ...prev, platformIds: next };
    });
  };

  const updateCreator = (index: number, name: string) => {
    setForm((prev) => {
      const next = [...prev.creators];
      next[index] = { name };
      return { ...prev, creators: next };
    });
  };

  const addCreator = () => {
    setForm((prev) => ({ ...prev, creators: [...prev.creators, { name: '' }] }));
  };

  const metadataValid =
    form.brandName.trim().length > 0 &&
    form.industryVertical.trim().length > 0 &&
    form.firstLineOfCopy.trim().length > 0 &&
    form.startDate.length > 0 &&
    form.endDate.length > 0 &&
    form.endDate >= form.startDate &&
    form.creators.some((c) => c.name.trim().length > 0);

  const handleNext = (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    setError(null);
    if (!metadataValid) {
      setError('Fill in every required field before continuing.');
      return;
    }
    setStep('linking');
  };

  const handleBack = (): void => {
    setError(null);
    setStep('metadata');
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const campaign = await createCampaign({
        ...form,
        creators: form.creators.filter((c) => c.name.trim().length > 0),
      });
      onCreated(campaign);
    } catch (err: any) {
      setError(err.message || 'Failed to create campaign');
    } finally {
      setSubmitting(false);
    }
  };

  const linkedPlatformCount = CG_BOOST_PLATFORMS.filter(
    (p) => Boolean(form.platformIds?.[p]),
  ).length;

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={`CG Boost · Step ${step === 'metadata' ? '1' : '2'} of 2`}
      title={step === 'metadata' ? 'New campaign' : 'Link platform campaigns'}
      width={760}
      footer={
        step === 'metadata' ? (
          <>
            <Button variant="ghost" size="md" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleNext}
              disabled={!metadataValid}
            >
              Next: Link platforms →
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" size="md" onClick={handleBack} disabled={submitting}>
              ← Back
            </Button>
            <div style={{ flex: 1, fontSize: 12, color: 'var(--fg-3)' }}>
              {linkedPlatformCount === 0
                ? 'No platforms linked — campaign will be created as a draft.'
                : `${linkedPlatformCount} of 3 platform${linkedPlatformCount === 1 ? '' : 's'} linked`}
            </div>
            <Button
              variant="primary"
              size="md"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Creating…' : 'Create campaign'}
            </Button>
          </>
        )
      }
    >
      {step === 'metadata' ? (
        <form onSubmit={handleNext} style={body}>
          <div style={grid2}>
            <Field label="Brand / Company *">
              <input
                style={input}
                value={form.brandName}
                onChange={(e) => update('brandName', e.target.value)}
                required
              />
            </Field>
            <Field label="Campaign name">
              <input
                style={input}
                value={form.campaignName}
                onChange={(e) => update('campaignName', e.target.value)}
                placeholder="e.g. Spring '26 Boost"
              />
            </Field>

            <Field label="Industry vertical *">
              <select
                style={input}
                value={form.industryVertical}
                onChange={(e) => update('industryVertical', e.target.value)}
                required
              >
                <option value="">Select…</option>
                {VERTICALS.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </Field>
            <Field label="First line of copy *">
              <input
                style={input}
                value={form.firstLineOfCopy}
                onChange={(e) => update('firstLineOfCopy', e.target.value)}
                required
              />
            </Field>

            <Field label="Start date *">
              <input
                type="date"
                style={input}
                value={form.startDate}
                onChange={(e) => update('startDate', e.target.value)}
                required
              />
            </Field>
            <Field label="End date *">
              <input
                type="date"
                style={input}
                value={form.endDate}
                onChange={(e) => update('endDate', e.target.value)}
                required
              />
            </Field>
          </div>

          <Field label="Creators *">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {form.creators.map((c, i) => (
                <input
                  key={i}
                  style={input}
                  value={c.name}
                  onChange={(e) => updateCreator(i, e.target.value)}
                  placeholder={`Creator ${i + 1}`}
                />
              ))}
              <Button type="button" variant="ghost" size="sm" onClick={addCreator} style={{ alignSelf: 'flex-start' }}>
                + Add creator
              </Button>
            </div>
          </Field>

          <Field label="Planned budget (USD)">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <BudgetField label="TikTok" value={form.plannedBudget.tiktok ?? 0} onChange={(v) => updateBudget('tiktok', v)} />
              <BudgetField label="Meta" value={form.plannedBudget.meta ?? 0} onChange={(v) => updateBudget('meta', v)} />
              <BudgetField label="YouTube" value={form.plannedBudget.youtube ?? 0} onChange={(v) => updateBudget('youtube', v)} />
            </div>
          </Field>

          {error && <div style={errorBox}>{error}</div>}
        </form>
      ) : (
        <div style={body}>
          <div style={hint}>
            <Pill tone="info" size="sm">Optional</Pill>
            <span style={{ fontSize: 12.5, color: 'var(--fg-3)', lineHeight: 1.5 }}>
              You can save the campaign without linking any platforms — it'll be created as a draft.
              Linking can be added or edited later. Validation is best-effort: if credentials aren't
              configured, the link is saved unchecked and you'll see a warning badge on the dashboard.
            </span>
          </div>

          {CG_BOOST_PLATFORMS.map((platform) => (
            <PlatformLinkCard
              key={platform}
              platform={platform}
              value={form.platformIds?.[platform] as AnyPlatformIds | undefined}
              onChange={(ids) => updatePlatformIds(platform, ids)}
            />
          ))}

          {error && <div style={errorBox}>{error}</div>}
        </div>
      )}
    </Modal>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={fieldLabel}>{label}</span>
    {children}
  </label>
);

const BudgetField: React.FC<{ label: string; value: number; onChange: (v: number) => void }> = ({ label, value, onChange }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <span style={{ ...fieldLabel, fontSize: 11 }}>{label}</span>
    <input
      type="number"
      min={0}
      style={{ ...input, fontVariantNumeric: 'tabular-nums' }}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  </label>
);

const body: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 18,
};

const grid2: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14,
};

const fieldLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--fg-3)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
};

const input: React.CSSProperties = {
  padding: '9px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-2)',
  fontSize: 13.5,
  color: 'var(--fg-1)',
  background: '#fff',
  fontFamily: 'var(--font-sans)',
  boxShadow: 'var(--shadow-inset)',
  outline: 'none',
};

const errorBox: React.CSSProperties = {
  background: 'var(--danger-soft)',
  color: 'var(--danger)',
  padding: 12,
  borderRadius: 'var(--radius-md)',
  fontSize: 13,
  border: '1px solid var(--cg-terra-200)',
};

const hint: React.CSSProperties = {
  background: 'var(--info-soft)',
  padding: 12,
  borderRadius: 'var(--radius-md)',
  fontSize: 12.5,
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
};

export default NewCampaignModal;
